import React, { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import API_BASE_URL from "../config/api";

import "swiper/css";
import "swiper/css/navigation";
import "./student-say.css";

const API_BASE = API_BASE_URL;
const TEXT_LIMIT = 120;

const StudentSay = () => {
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  const [testimonials, setTestimonials] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);

  /* =========================
     TOGGLE READ MORE
  ========================= */
  const toggleReadMore = (id) => {
    setExpanded((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  /* =========================
     FETCH TESTIMONIALS
  ========================= */
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const res = await fetch(`${API_BASE}/get_testimonial.php`);
        const data = await res.json();

        if (data?.success && Array.isArray(data.states)) {
          const formatted = data.states
            .filter((t) => t.is_active === "Y")
            .map((t) => ({
              id: t.id,
              name: t.student_name,
              title: t.title,
              text: t.description || "",
              rating: Number(t.rating) || 0,
              date: new Date(t.created_on).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "long",
                year: "numeric",
              }),
              avatar: t.img ? `${API_BASE}${t.img}` : "",
            }));

          setTestimonials(formatted);
        }
      } catch (err) {
        console.error("Testimonial fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  if (loading || testimonials.length === 0) return null;

  return (
    <section className="student-say py-5">
      <div className="container position-relative">

        {/* HEADER */}
        <div className="text-center mb-4">
          <h2 className="fw-bold">What Our Students Say</h2>
          <p className="text-muted">
            Real experiences from pilots who trained with us
          </p>
        </div>

        {/* CUSTOM NAV */}
        <button ref={prevRef} className="review-nav prev">‹</button>
        <button ref={nextRef} className="review-nav next">›</button>

        <Swiper
          modules={[Autoplay, Navigation]}
          spaceBetween={24}
          autoplay={{ delay: 4000, disableOnInteraction: false }}
          onBeforeInit={(swiper) => {
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;
          }}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          breakpoints={{
            0: { slidesPerView: 1 },
            768: { slidesPerView: 2 },
            1200: { slidesPerView: 4 },
          }}
        >
          {testimonials.map((t) => {
            const isExpanded = expanded[t.id];
            const showReadMore = t.text.length > TEXT_LIMIT;

            return (
              <SwiperSlide key={t.id}>
                <div className="review-card">

                  {/* HEADER */}
                  <div className="review-header">
                    <div className="d-flex align-items-center">
                      {t.avatar ? (
                        <img
                          src={t.avatar}
                          alt={t.name}
                          className="review-avatar"
                        />
                      ) : (
                        <div className="review-avatar letter">
                          {t.name?.charAt(0)}
                        </div>
                      )}

                      <div className="ms-3">
                        <h6 className="mb-0">{t.name}</h6>
                       {/* <span className="review-date">{t.date}</span>*/}
                      </div>
                    </div>

                    <img
                      className="google-icon"
                      src={`${process.env.PUBLIC_URL}/assets/img/google.svg`}
                      alt="Google"
                    />
                  </div>

                  {/* STARS */}
                  <div className="review-stars">
                    {[...Array(5)].map((_, i) => (
                      <i
                        key={i}
                        className={`fas fa-star ${
                          i < t.rating ? "active" : ""
                        }`}
                      ></i>
                    ))}
                    <i className="fas fa-check-circle verified"></i>
                  </div>

                  {/* TEXT */}
                  <p className="review-text">
                    {isExpanded
                      ? t.text
                      : showReadMore
                      ? `${t.text.slice(0, TEXT_LIMIT)}...`
                      : t.text}
                  </p>

                  {showReadMore && (
                    <span
                      className="read-more"
                      onClick={() => toggleReadMore(t.id)}
                    >
                      {isExpanded ? "Read less" : "Read more"}
                    </span>
                  )}

                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </section>
  );
};

export default StudentSay;
